import { useEffect, useMemo, useRef, useState, type JSX } from "react";
import { GameApiClient } from "./api/client";
import type {
  AdminApprovalRequest,
  AdminCaseUpdate,
  AdminPendingCaseDto,
  AuthConfigDto,
  CaseGenerationBatchDto,
  CaseDto,
  CaseSelection,
  ConversationEntry,
  EvaluationEvent,
  GenerateCasesRequest,
  GameUserDto,
  HintEvent,
  SessionCompletedEvent,
  TaxonomySectorDto,
} from "./api/types";
import { createUserManager, getAccessToken, getUserManager } from "./auth/pkce";
import {
  prependUniqueGenerationBatch,
  selectPolledGenerationBatches,
} from "./generation-batches";
import { messages } from "./messages";
import { AdminReviewPage } from "./pages/AdminReviewPage";
import { CompletePage } from "./pages/CompletePage";
import { LoginPage } from "./pages/LoginPage";
import { PlayPage } from "./pages/PlayPage";
import { SelectionPage } from "./pages/SelectionPage";

type View = "login" | "selection" | "play" | "complete" | "admin";

const ADMIN_PATH = "/admin/review";

async function loadAuthConfig(): Promise<AuthConfigDto> {
  const response = await fetch("/api/auth/config");
  if (!response.ok)
    throw new Error(messages.loginError);
  return await response.json() as AuthConfigDto;
}

function isHintLevel(level: number): level is 1 | 2 | 3 {
  return level === 1 || level === 2 || level === 3;
}

/** Coordinates authentication, player flow, SSE events, and admin review. */
export function App(): JSX.Element {
  const [view, setView] = useState<View>("login");
  const [isBusy, setIsBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [taxonomy, setTaxonomy] = useState<readonly TaxonomySectorDto[]>([]);
  const [pendingCases, setPendingCases] = useState<readonly AdminPendingCaseDto[]>([]);
  const [generationBatches, setGenerationBatches] = useState<readonly CaseGenerationBatchDto[]>([]);
  const [users, setUsers] = useState<readonly GameUserDto[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [generateMessage, setGenerateMessage] = useState<string | null>(null);
  const [adminRefreshError, setAdminRefreshError] = useState<string | null>(null);
  const generationBatchesRevision = useRef(0);
  const [gameCase, setGameCase] = useState<CaseDto | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [entries, setEntries] = useState<readonly ConversationEntry[]>([]);
  const [evaluation, setEvaluation] = useState<EvaluationEvent | null>(null);
  const [hintLevel, setHintLevel] = useState<0 | 1 | 2 | 3>(0);
  const [completion, setCompletion] = useState<SessionCompletedEvent | null>(null);
  const entrySequence = useRef(0);

  const api = useMemo(
    () => new GameApiClient(getAccessToken),
    [],
  );

  useEffect(() => {
    const start = async (): Promise<void> => {
      setIsBusy(true);
      setError(null);
      try {
        const config = await loadAuthConfig();
        const userManager = createUserManager(config);
        if (window.location.pathname === "/callback") {
          await userManager.signinRedirectCallback();
          window.history.replaceState({}, document.title, "/");
        }
        const accessToken = await getAccessToken();
        if (accessToken === null) {
          setView("login");
          return;
        }

        const authenticatedApi = new GameApiClient(() => accessToken);
        const currentUser = await authenticatedApi.getCurrentUser();
        setIsAdmin(currentUser.isAdmin);
        if (window.location.pathname === ADMIN_PATH && !currentUser.isAdmin) {
          setError("Du har ikke tilgang til faglig kontroll.");
          window.history.replaceState({}, document.title, "/");
          setTaxonomy(await authenticatedApi.getTaxonomy());
          setView("selection");
          return;
        }

        setTaxonomy(await authenticatedApi.getTaxonomy());
        if (window.location.pathname === ADMIN_PATH) {
          const [nextCases, nextUsers, nextBatches] = await Promise.all([
            authenticatedApi.getPendingAdminCases(),
            authenticatedApi.getAdminUsers(),
            authenticatedApi.getCaseGenerationBatches(),
          ]);
          setPendingCases(nextCases);
          setUsers(nextUsers);
          setGenerationBatches(nextBatches);
          setView("admin");
        } else {
          setView("selection");
        }
      } catch {
        setError(messages.loginError);
        setView("login");
      } finally {
        setIsBusy(false);
      }
    };

    void start();
  }, []);

  useEffect(() => {
    if (view !== "admin")
      return undefined;

    let isActive = true;
    let isRefreshing = false;
    const refreshAdminStatus = async (): Promise<void> => {
      if (isRefreshing)
        return;
      isRefreshing = true;
      const requestRevision = generationBatchesRevision.current;
      try {
        const [nextBatches, nextCases] = await Promise.all([
          api.getCaseGenerationBatches(),
          api.getPendingAdminCases(),
        ]);
        if (!isActive)
          return;
        setGenerationBatches((current) => selectPolledGenerationBatches(
          current,
          nextBatches,
          requestRevision,
          generationBatchesRevision.current,
        ));
        setPendingCases(nextCases);
        setAdminRefreshError(null);
      } catch (cause) {
        if (isActive)
          setAdminRefreshError(cause instanceof Error ? cause.message : messages.genericError);
      } finally {
        isRefreshing = false;
      }
    };

    const intervalId = window.setInterval(() => {
      void refreshAdminStatus();
    }, 5000);
    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, [api, view]);

  const login = async (): Promise<void> => {
    setIsBusy(true);
    try {
      await getUserManager().signinRedirect();
    } catch {
      setError(messages.loginError);
      setIsBusy(false);
    }
  };

  const logout = async (): Promise<void> => {
    setGameCase(null);
    setSessionId(null);
    setEntries([]);
    setEvaluation(null);
    setCompletion(null);
    setIsAdmin(false);
    setUsers([]);
    generationBatchesRevision.current++;
    setGenerationBatches([]);
    setAdminRefreshError(null);
    setGenerateMessage(null);
    setView("login");
    try {
      await getUserManager().removeUser();
    } catch {
      setError(messages.genericError);
    }
  };

  const startCase = async (selection: CaseSelection): Promise<void> => {
    setIsBusy(true);
    setError(null);
    try {
      const selected = await api.getRandomCase(selection);
      const session = await api.startSession(selected.id);
      setGameCase(selected);
      setSessionId(session.id);
      setEntries([{
        id: "gatekeeper-opening",
        kind: "gatekeeper",
        text: selected.gatekeeperOpening,
      }]);
      setEvaluation(null);
      setHintLevel(0);
      setCompletion(null);
      setView("play");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : messages.noCases);
    } finally {
      setIsBusy(false);
    }
  };

  const sendArgument = async (content: string): Promise<void> => {
    if (sessionId === null)
      return;
    entrySequence.current += 1;
    setEntries((current) => [
      ...current,
      { id: `player-${entrySequence.current}`, kind: "player", text: content },
    ]);
    setIsBusy(true);
    setError(null);
    try {
      await api.sendArgument(sessionId, content, (event) => {
        if (event.event === "evaluation") {
          const evaluation = JSON.parse(event.data) as EvaluationEvent;
          setEvaluation(evaluation);
          entrySequence.current += 1;
          setEntries((current) => [
            ...current,
            {
              id: `evaluation-${entrySequence.current}`,
              kind: "evaluation",
              text: evaluation.feedback,
            },
          ]);
        }
        if (event.event === "session_completed") {
          const completed = JSON.parse(event.data) as SessionCompletedEvent;
          setCompletion(completed);
          setView("complete");
        }
        if (event.event === "error")
          setError(messages.genericError);
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : messages.genericError);
    } finally {
      setIsBusy(false);
    }
  };

  const requestHint = async (): Promise<void> => {
    if (sessionId === null)
      return;
    setIsBusy(true);
    setError(null);
    try {
      await api.requestHint(sessionId, (event) => {
        if (event.event === "hint") {
          const payload = JSON.parse(event.data) as HintEvent;
          if (!isHintLevel(payload.level))
            throw new Error("Hintnivået fra serveren er ugyldig.");
          setHintLevel(payload.level);
          entrySequence.current += 1;
          setEntries((current) => [
            ...current,
            {
              id: `hint-${entrySequence.current}`,
              kind: "hint",
              text: payload.text,
              level: payload.level,
            },
          ]);
        }
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : messages.genericError);
    } finally {
      setIsBusy(false);
    }
  };

  const giveUp = async (): Promise<void> => {
    if (sessionId === null)
      return;
    setIsBusy(true);
    setError(null);
    try {
      const result = await api.giveUp(sessionId);
      setCompletion(result);
      setView("complete");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : messages.genericError);
    } finally {
      setIsBusy(false);
    }
  };

  const openAdmin = async (): Promise<void> => {
    if (!isAdmin) {
      setError("Du har ikke tilgang til faglig kontroll.");
      return;
    }

    setIsBusy(true);
    setError(null);
    setGenerateMessage(null);
    setAdminRefreshError(null);
    try {
      const [nextCases, nextUsers, nextBatches] = await Promise.all([
        api.getPendingAdminCases(),
        api.getAdminUsers(),
        api.getCaseGenerationBatches(),
      ]);
      setPendingCases(nextCases);
      setUsers(nextUsers);
      setGenerationBatches(nextBatches);
      window.history.replaceState({}, document.title, ADMIN_PATH);
      setView("admin");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : messages.genericError);
    } finally {
      setIsBusy(false);
    }
  };

  const generateCases = async (request: GenerateCasesRequest): Promise<void> => {
    setIsBusy(true);
    setError(null);
    try {
      const batch = await api.generateCases(request);
      generationBatchesRevision.current++;
      setGenerationBatches((current) => prependUniqueGenerationBatch(current, batch));
      setGenerateMessage(
        `${batch.requestedCount.toLocaleString("nb-NO")} caser er lagt i kø. Du kan forlate siden mens jobben kjører.`,
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : messages.genericError);
    } finally {
      setIsBusy(false);
    }
  };

  const setUserRole = async (userId: string, role: GameUserDto["role"]): Promise<void> => {
    setIsBusy(true);
    setError(null);
    try {
      const updated = await api.setUserRole(userId, role);
      setUsers((current) => current.map((user) => (user.id === updated.id ? updated : user)));
      const currentUser = await api.getCurrentUser();
      setIsAdmin(currentUser.isAdmin);
      if (!currentUser.isAdmin) {
        window.history.replaceState({}, document.title, "/");
        setView("selection");
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : messages.genericError);
    } finally {
      setIsBusy(false);
    }
  };

  const savePendingCase = async (caseId: string, update: AdminCaseUpdate): Promise<void> => {
    setIsBusy(true);
    setError(null);
    try {
      const updated = await api.updatePendingCase(caseId, update);
      setPendingCases((current) => current.map((gameCase) => (
        gameCase.id === updated.id ? updated : gameCase
      )));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : messages.genericError);
    } finally {
      setIsBusy(false);
    }
  };

  const rejectPendingCase = async (caseId: string, reason: string): Promise<void> => {
    setIsBusy(true);
    setError(null);
    try {
      await api.rejectPendingCase(caseId, reason);
      setPendingCases((current) => current.filter((gameCase) => gameCase.id !== caseId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : messages.genericError);
    } finally {
      setIsBusy(false);
    }
  };

  const approvePendingCase = async (
    caseId: string,
    approval: AdminApprovalRequest,
  ): Promise<void> => {
    setIsBusy(true);
    setError(null);
    try {
      await api.approvePendingCase(caseId, approval);
      setPendingCases((current) => current.filter((gameCase) => gameCase.id !== caseId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : messages.genericError);
    } finally {
      setIsBusy(false);
    }
  };

  switch (view) {
    case "login":
      return <LoginPage onLogin={() => { void login(); }} isBusy={isBusy} error={error} />;
    case "selection":
      return (
        <SelectionPage
          taxonomy={taxonomy}
          onStart={(selection) => { void startCase(selection); }}
          isBusy={isBusy}
          error={error}
          isAdmin={isAdmin}
          onOpenAdmin={() => { void openAdmin(); }}
          onLogout={() => { void logout(); }}
        />
      );
    case "admin":
      return (
        <AdminReviewPage
          cases={pendingCases}
          users={users}
          taxonomy={taxonomy}
          generationBatches={generationBatches}
          refreshError={adminRefreshError}
          isBusy={isBusy}
          error={error}
          generateMessage={generateMessage}
          onGenerate={generateCases}
          onSetRole={setUserRole}
          onSave={savePendingCase}
          onReject={rejectPendingCase}
          onApprove={approvePendingCase}
          onLogout={() => { void logout(); }}
        />
      );
    case "complete":
      if (gameCase !== null && completion !== null) {
        return (
          <CompletePage
            title={gameCase.title}
            completion={completion}
            onPlayAgain={() => { setView("selection"); }}
            onLogout={() => { void logout(); }}
          />
        );
      }
      return <LoginPage onLogin={() => { void login(); }} isBusy={isBusy} error={error} />;
    case "play":
      if (gameCase !== null) {
        return (
          <PlayPage
            gameCase={gameCase}
            entries={entries}
            evaluation={evaluation}
            hintLevel={hintLevel}
            isBusy={isBusy}
            error={error}
            onSend={sendArgument}
            onHint={requestHint}
            onGiveUp={giveUp}
            onLogout={() => { void logout(); }}
          />
        );
      }
      return <LoginPage onLogin={() => { void login(); }} isBusy={isBusy} error={error} />;
    default: {
      const exhaustiveCheck: never = view;
      return exhaustiveCheck;
    }
  }
}
